import React, { useState, useEffect } from 'react';
import { 
  Building,
  Users,
  CreditCard,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Key,
  MessageSquare,
  Zap,
  Euro,
  Activity,
  Calendar,
  RefreshCw,
  AlertTriangle,
  Clock,
  Eye,
  X
} from 'lucide-react';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { adminApi } from '../../services/api';
import { AdminSystemStats } from '../../types';

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  trend?: { value: number; label: string };
  color: 'primary' | 'success' | 'warning' | 'error' | 'info';
  subtitle?: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, trend, color, subtitle }) => {
  const colorClasses = {
    primary: 'text-primary bg-primary/10',
    success: 'text-green-500 bg-green-500/10',
    warning: 'text-yellow-500 bg-yellow-500/10',
    error: 'text-red-500 bg-red-500/10',
    info: 'text-blue-500 bg-blue-500/10',
  };

  return (
    <div className="dark:bg-surface bg-light-surface rounded-xl p-6 border dark:border-border-color border-light-border-color">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm dark:text-text-secondary text-light-text-secondary">{title}</p>
          <p className="text-3xl font-bold mt-1 dark:text-white text-gray-900">{value}</p>
          {subtitle && (
            <p className="text-sm mt-1 dark:text-text-secondary text-light-text-secondary">{subtitle}</p>
          )}
          {trend && (
            <div className={`flex items-center gap-1 mt-1 text-sm ${trend.value >= 0 ? 'text-green-500' : 'text-red-500'}`}>
              {trend.value >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
              <span>{trend.value >= 0 ? '+' : ''}{trend.value.toFixed(1)}% {trend.label}</span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
          {icon}
        </div>
      </div>
    </div>
  );
};

interface MetricRowProps {
  label: string;
  value: string | number;
  subValue?: string;
}

const MetricRow: React.FC<MetricRowProps> = ({ label, value, subValue }) => (
  <div className="flex items-center justify-between py-3 border-b dark:border-border-color border-light-border-color last:border-b-0">
    <span className="dark:text-text-secondary text-light-text-secondary">{label}</span>
    <div className="text-right">
      <span className="font-semibold dark:text-white text-gray-900">{value}</span>
      {subValue && (
        <p className="text-xs dark:text-text-secondary text-light-text-secondary">{subValue}</p>
      )}
    </div>
  </div>
);

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatNumber = (num: number): string => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
};

const SystemStatsPage: React.FC = () => {
  const [stats, setStats] = useState<AdminSystemStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showEcommerceModal, setShowEcommerceModal] = useState(false);

  // Mock data for tenant types - in real app this would come from API
  const tenantTypeBreakdown = {
    ecommerce: {
      total: 2,
      subtypes: {
        grocery: 1,
        tech: 1,
      }
    },
    internal: 2,
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async (forceRefresh: boolean = false) => {
    try {
      if (forceRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      const data = await adminApi.getSystemStats(forceRefresh);
      setStats(data);
      setError(null);
    } catch (err) {
      setError('Failed to load system statistics');
      console.error(err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-6 flex items-center gap-4">
        <AlertTriangle className="w-8 h-8 text-red-500" />
        <div>
          <p className="font-semibold text-red-500">Error Loading Statistics</p>
          <p className="text-sm dark:text-text-secondary text-light-text-secondary">{error}</p>
        </div>
        <Button variant="secondary" onClick={() => fetchStats()} className="ml-auto">
          Retry
        </Button>
      </div>
    );
  }

  if (!stats) return null;

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <BarChart3 className="w-8 h-8 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold dark:text-white text-gray-900">
              System Analytics
            </h1>
            <p className="dark:text-text-secondary text-light-text-secondary">
              Cross-tenant system-wide statistics and metrics
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-sm dark:text-text-secondary text-light-text-secondary">
            <Clock className="w-4 h-4" />
            <span>Last updated: {new Date(stats.computedAt).toLocaleString()}</span>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => fetchStats(true)}
            disabled={refreshing}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>
      </div>

      {/* Quick Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Tenants (Active)"
          value={`${stats.tenants.total} (${stats.tenants.active})`}
          icon={<Building className="w-6 h-6" />}
          color="primary"
        />
        <StatCard
          title="Total Users"
          value={formatNumber(stats.users.totalUsers)}
          icon={<Users className="w-6 h-6" />}
          color="info"
          subtitle={`${stats.users.newUsersThisPeriod} new this period`}
        />
        <StatCard
          title="Monthly Revenue (MRR)"
          value={formatCurrency(stats.revenue.mrr)}
          icon={<Euro className="w-6 h-6" />}
          color="success"
        />
        <StatCard
          title="API Keys"
          value={stats.apiKeys.activeApiKeys}
          icon={<Key className="w-6 h-6" />}
          color="warning"
          subtitle={`${stats.apiKeys.totalApiKeys} total`}
        />
      </div>

      {/* Detailed Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Tenant Breakdown */}
        <Card>
          <div className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Building className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-semibold dark:text-white text-gray-900">
                Active Tenants Overview
              </h2>
            </div>
            <div className="space-y-1">
              <div className="flex items-center justify-between py-3 border-b dark:border-border-color border-light-border-color">
                <span className="dark:text-text-secondary text-light-text-secondary">E-Commerce</span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowEcommerceModal(true)}
                    className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 hover:text-primary transition-colors"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <span className="font-semibold dark:text-white text-gray-900">{tenantTypeBreakdown.ecommerce.total}</span>
                </div>
              </div>
              <MetricRow label="Internal Tool" value={tenantTypeBreakdown.internal} />
              <div className="flex items-center justify-between py-3 border-t-2 dark:border-primary/30 border-primary/30 mt-2">
                <span className="font-semibold dark:text-white text-gray-900">Total</span>
                <span className="font-bold text-primary text-lg">{stats.tenants.active}</span>
              </div>
            </div>
          </div>
        </Card>

        {/* User Breakdown */}
        <Card>
          <div className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Users className="w-5 h-5 text-blue-500" />
              <h2 className="text-lg font-semibold dark:text-white text-gray-900">
                User Statistics
              </h2>
            </div>
            <div className="space-y-1">
              <MetricRow label="Total Users" value={stats.users.activeUsers} />
              <MetricRow label="New This Period" value={stats.users.newUsersThisPeriod} />
              {Object.entries(stats.users.usersByType || {}).map(([type, count]) => (
                <MetricRow key={type} label={`${type.charAt(0).toUpperCase() + type.slice(1)} Users`} value={count} />
              ))}
            </div>
          </div>
        </Card>

        {/* Subscription Breakdown */}
        <Card>
          <div className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <CreditCard className="w-5 h-5 text-green-500" />
              <h2 className="text-lg font-semibold dark:text-white text-gray-900">
                Subscriptions
              </h2>
            </div>
            <div className="space-y-1">
              <MetricRow label="Active" value={stats.subscriptions.activeSubscriptions} />
              <MetricRow label="Inactive" value={stats.subscriptions.canceledSubscriptions} />
              <MetricRow label="Suspended" value={stats.subscriptions.pastDueSubscriptions} />
              <MetricRow label="Pending" value={0} />
              <MetricRow label="Trialing" value={0} />
            </div>
          </div>
        </Card>
      </div>

      {/* Revenue & Usage Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Metrics */}
        <Card>
          <div className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Euro className="w-5 h-5 text-green-500" />
              <h2 className="text-lg font-semibold dark:text-white text-gray-900">
                Revenue Metrics
              </h2>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="p-4 rounded-lg dark:bg-background bg-gray-50">
                <p className="text-sm dark:text-text-secondary text-light-text-secondary">MRR</p>
                <p className="text-2xl font-bold dark:text-white text-gray-900">{formatCurrency(stats.revenue.mrr)}</p>
              </div>
              <div className="p-4 rounded-lg dark:bg-background bg-gray-50">
                <p className="text-sm dark:text-text-secondary text-light-text-secondary">ARR</p>
                <p className="text-2xl font-bold dark:text-white text-gray-900">{formatCurrency(stats.revenue.arr)}</p>
              </div>
            </div>
            <div className="space-y-1">
              <div className="mb-3">
                <p className="text-sm font-medium dark:text-white text-gray-900 mb-2">ARPU</p>
                <MetricRow 
                  label="Per Month" 
                  value={formatCurrency(stats.revenue.averageRevenuePerUser)} 
                />
                <MetricRow 
                  label="Per Year" 
                  value={formatCurrency(stats.revenue.averageRevenuePerUser * 12)} 
                />
              </div>
            </div>
            <div className="mt-4 pt-4 border-t dark:border-border-color border-light-border-color">
              <p className="text-sm font-medium dark:text-white text-gray-900 mb-2">Revenue by Plan</p>
              <MetricRow 
                label="Basic" 
                value={formatCurrency(stats.revenue.revenueByPlan?.find(p => p.planId.toLowerCase().includes('basic'))?.revenue || 0)} 
              />
              <MetricRow 
                label="Premium" 
                value={formatCurrency(stats.revenue.revenueByPlan?.find(p => p.planId.toLowerCase().includes('premium'))?.revenue || 0)} 
              />
              <MetricRow 
                label="Enterprise" 
                value={formatCurrency(stats.revenue.revenueByPlan?.find(p => p.planId.toLowerCase().includes('enterprise'))?.revenue || 0)} 
              />
              <div className="flex items-center justify-between py-3 border-t-2 dark:border-primary/30 border-primary/30 mt-2">
                <span className="font-semibold dark:text-white text-gray-900">Total Revenue</span>
                <span className="font-bold text-primary text-lg">{formatCurrency(stats.revenue.totalRevenue)}</span>
              </div>
            </div>
          </div>
        </Card>

        {/* Usage Metrics */}
        <Card>
          <div className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Activity className="w-5 h-5 text-blue-500" />
              <h2 className="text-lg font-semibold dark:text-white text-gray-900">
                Usage Metrics
              </h2>
            </div>
            <div className="grid grid-cols-4 gap-4 mb-4">
              <div className="p-4 rounded-lg dark:bg-background bg-gray-50">
                <p className="text-xs dark:text-text-secondary text-light-text-secondary">Conversations (cycle)</p>
                <p className="text-xl font-bold dark:text-white text-gray-900">{formatNumber(stats.usage.totalConversations)}</p>
              </div>
              <div className="p-4 rounded-lg dark:bg-background bg-gray-50">
                <p className="text-xs dark:text-text-secondary text-light-text-secondary">Messages (cycle)</p>
                <p className="text-xl font-bold dark:text-white text-gray-900">{formatNumber(stats.usage.totalMessages)}</p>
              </div>
              <div className="p-4 rounded-lg dark:bg-background bg-gray-50">
                <p className="text-xs dark:text-text-secondary text-light-text-secondary">API Calls (cycle)</p>
                <p className="text-xl font-bold dark:text-white text-gray-900">{formatNumber(stats.usage.totalApiCalls)}</p>
              </div>
              <div className="p-4 rounded-lg dark:bg-background bg-gray-50">
                <p className="text-xs dark:text-text-secondary text-light-text-secondary">Storage</p>
                <p className="text-xl font-bold dark:text-white text-gray-900">{formatNumber(stats.usage.totalStorage)}</p>
              </div>
            </div>
            {stats.usage.topTenantsByUsage && stats.usage.topTenantsByUsage.length > 0 && (
              <div className="space-y-1">
                <p className="text-sm font-medium dark:text-white text-gray-900 mb-2">Top Tenants by Usage</p>
                {stats.usage.topTenantsByUsage.slice(0, 5).map((tenant) => (
                  <MetricRow 
                    key={tenant.tenantId} 
                    label={tenant.tenantName} 
                    value={formatNumber(tenant.totalUsage)} 
                  />
                ))}
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* E-Commerce Breakdown Modal */}
      {showEcommerceModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-surface w-full max-w-md rounded-xl shadow-2xl border dark:border-border-color border-light-border-color animate-in fade-in zoom-in duration-200">
            <div className="p-6 border-b dark:border-border-color border-light-border-color flex justify-between items-center">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Building className="w-5 h-5 text-primary" />
                </div>
                <h2 className="text-xl font-bold dark:text-white text-gray-900">
                  E-Commerce Breakdown
                </h2>
              </div>
              <button
                onClick={() => setShowEcommerceModal(false)}
                className="p-2 rounded-lg text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-1">
              <MetricRow label="Grocery" value={tenantTypeBreakdown.ecommerce.subtypes.grocery} />
              <MetricRow label="Tech" value={tenantTypeBreakdown.ecommerce.subtypes.tech} />
              <div className="flex items-center justify-between py-3 border-t-2 dark:border-primary/30 border-primary/30 mt-2">
                <span className="font-semibold dark:text-white text-gray-900">Total E-Commerce</span>
                <span className="font-bold text-primary text-lg">{tenantTypeBreakdown.ecommerce.total}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemStatsPage;
